import useReport from '@/components/reports/useReport';
import { useInfiniteTransactions } from '@/hooks/use-infinite-transactions';
import appServices from '@/services/appServices';
import { uiStateProxy } from '@/state/ui-state';
import { AppTransaction } from '@polybank/shared';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { useTrackedSnapshot } from 'valtio-select';

export default function useTransaction(transactionId: string): {
  transaction: AppTransaction | undefined;
  isLoading: boolean;
} {
  const reportId = useTrackedSnapshot(
    uiStateProxy,
    (state) => state.reportIdForDetails
  );
  const { transactionsMap } = useInfiniteTransactions();
  const { reportData } = useReport(reportId);

  const localTransaction = useMemo(() => {
    if (reportId) {
      return Object.values(reportData?.categories ?? {})
        ?.map(
          (category: { transactions: AppTransaction[] }) =>
            category.transactions
        )
        .flat()
        .find((t: AppTransaction) => t.$id === transactionId);
    }
    return transactionsMap[transactionId];
  }, [reportId, transactionsMap, transactionId, reportData?.categories]);

  const { data: remoteTransaction, isLoading: isRemoteLoading } = useQuery({
    queryKey: ['transaction', transactionId],
    queryFn: () => appServices.transactions.getTransaction(transactionId),
    enabled: !localTransaction && !!transactionId,
    staleTime: 5 * 60 * 1000,
  });

  const transaction = localTransaction ?? remoteTransaction ?? undefined;
  const isLoading = !localTransaction && !!transactionId && isRemoteLoading;

  return { transaction, isLoading };
}
